import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileSpreadsheet, Plus, Trash2, Database, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface UploadBatch {
  id: string;
  fileName: string;
  rowCount: number;
  uploadedAt: string;
  invoiceDateRange?: { min: string; max: string };
}

interface DataManagementPanelProps {
  batches: UploadBatch[];
  onUpload: () => void;
  onDelete: (batchId: string) => void;
  loading?: boolean;
}

function formatUploadDate(iso: string): string {
  try {
    return format(parseISO(iso), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return iso;
  }
}

function formatInvoiceDateRange(range: { min: string; max: string }): string {
  try {
    const min = format(parseISO(range.min), "MMM d, yyyy");
    const max = format(parseISO(range.max), "MMM d, yyyy");
    return min === max ? min : `${min} – ${max}`;
  } catch {
    return `${range.min} – ${range.max}`;
  }
}

export function DataManagementPanel({
  batches,
  onUpload,
  onDelete,
  loading,
}: DataManagementPanelProps) {
  const [pendingDelete, setPendingDelete] = useState<UploadBatch | null>(null);

  return (
    <>
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Data</span>
            {batches.length > 0 && (
              <span className="text-xs text-muted-foreground">
                · {batches.length} dataset{batches.length !== 1 ? "s" : ""} loaded
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" onClick={onUpload}>
            <Plus className="w-3.5 h-3.5" />
            Upload Data
          </Button>
        </div>

        {/* Dataset cards or empty state */}
        {loading && batches.length === 0 ? (
          <div
            className={cn(
              "rounded-lg border border-dashed border-border",
              "flex flex-col items-center justify-center py-8 gap-2 text-center"
            )}
          >
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            <p className="text-sm text-muted-foreground">Loading datasets…</p>
          </div>
        ) : batches.length === 0 ? (
          <div
            className={cn(
              "rounded-lg border border-dashed border-border",
              "flex flex-col items-center justify-center py-8 gap-2 text-center"
            )}
          >
            <FileSpreadsheet className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No datasets uploaded yet</p>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-primary h-7 px-3"
              onClick={onUpload}
            >
              Upload your first CSV or XLSX
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {batches.map((batch) => (
              <Card key={batch.id} className="border-border/60 shadow-none">
                <CardContent className="flex items-center gap-3 py-3 px-4">
                  <FileSpreadsheet className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{batch.fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      Uploaded {formatUploadDate(batch.uploadedAt)} &middot;{" "}
                      {batch.rowCount.toLocaleString()} invoice{batch.rowCount !== 1 ? "s" : ""}
                    </p>
                    {batch.invoiceDateRange && (
                      <p className="text-xs text-muted-foreground">
                        Invoices: {formatInvoiceDateRange(batch.invoiceDateRange)}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setPendingDelete(batch)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
            <p className="text-[11px] text-muted-foreground px-0.5">
              Loaded datasets power all three tabs below.
            </p>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove dataset?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all{" "}
              <strong>{pendingDelete?.rowCount.toLocaleString()}</strong> invoices from{" "}
              <strong>{pendingDelete?.fileName}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDelete) {
                  onDelete(pendingDelete.id);
                  setPendingDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
